import { Col, Button, Form, Card } from "react-bootstrap";
import "../../../App.css";
import "../../../css/bootstrap.scss";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  setwizardPageNumber,
  setwizardPageName,
  setCheckPage,
} from "../../EntityAccountReducer";
import { notifyError } from "MyComponents/Toaster";
import { updateEntityKycDraftStep, uploadEntityKycDraftFiles } from "../../../Utils/entityKycDraft";
import CountryDropdown from "MyComponents/UI/CountryDropdown";
import countriesStateData from "../../../data/states.json";
import countriesData from "../../../data/countries.json";
import MultiFileSelector from "./FilePicker";
import { YearPicker, MonthPicker, DayPicker } from "react-dropdown-date";

interface UploadedFile {
  filename: string;
  url: string;
}

interface Owner {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  percentageOfOwnership: string;
  placeOfBirth?: string;
  nationality?: string;
  countryOfResidence?: string;
  identityDocumentUrl?: string;
}

const BeneficialOwnersForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userIDFromRedux = useSelector((state: RootState) => state.entityAccount.userID);
  const { userId: userIdFromUrl } = useParams<{ userId?: string }>();
  const ssoUserID = userIDFromRedux || userIdFromUrl || "";
  const checkPage = useSelector((state: RootState) => state.entityAccount.checkPage);

  const [owners, setOwners] = useState<Owner[]>([]);
  const [showAddForm, setShowAddForm] = useState(true);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Form states
  const [isLoading, setIsLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState({ year: "", month: "", day: "" });
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [percentageOfOwnership, setPercentageOfOwnership] = useState("");
  
  // > 20% fields
  const [placeOfBirth, setPlaceOfBirth] = useState("");
  const [nationality, setNationality] = useState("");
  const [countryOfResidence, setCountryOfResidence] = useState("");
  const [uploadedDocument, setUploadedDocument] = useState<UploadedFile[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const [errors, setErrors] = useState<any>({});

  const invites = useSelector((state: RootState) => state.entityAccount.invites);

  useEffect(() => {
    dispatch(setwizardPageName("beneficialOwners"));
  }, [dispatch]);

  useEffect(() => {
    if (invites && invites.length > 0) {
      const existingOwners = invites.filter((inv: any) => inv?.role === 'beneficialOwner' || inv?.Role === 'beneficialOwner' || inv?.userRole === 'beneficialOwner');
      if (existingOwners.length > 0) {
        setOwners(existingOwners as any);
        setShowAddForm(false);
      }
    }
  }, [invites]);

  const resetForm = () => {
    setFirstName(""); setMiddleName(""); setLastName(""); setEmail("");
    setDate({ year: "", month: "", day: "" });
    setStreetAddress(""); setCity(""); setSelectedState(""); setPostalCode(""); setSelectedCountry("");
    setPercentageOfOwnership(""); setPlaceOfBirth(""); setNationality(""); setCountryOfResidence("");
    setUploadedDocument([]); setPendingFiles([]);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: any = {};
    if (!firstName) newErrors.firstName = "First name is required";
    if (!lastName) newErrors.lastName = "Last name is required";
    if (!email) newErrors.email = "Email is required";
    if (!date.year || !date.month || !date.day) newErrors.dob = "Date of Birth is required";
    if (!streetAddress) newErrors.streetAddress = "Street address is required";
    if (!city) newErrors.city = "City is required";
    if (!selectedState) newErrors.state = "State is required";
    if (!postalCode) newErrors.postalCode = "Postal code is required";
    if (!selectedCountry) newErrors.country = "Country is required";
    if (!percentageOfOwnership) newErrors.percentageOfOwnership = "Percentage of ownership is required";

    const p = parseFloat(percentageOfOwnership);
    if (!isNaN(p) && p > 20) {
      if (!placeOfBirth) newErrors.placeOfBirth = "Place of Birth is required";
      if (!nationality) newErrors.nationality = "Nationality is required";
      if (!countryOfResidence) newErrors.countryOfResidence = "Country of Residence is required";
      if (uploadedDocument.length === 0 && pendingFiles.length === 0) {
        newErrors.document = "Identity document is required for ownership > 20%";
      }
    }
    setErrors(newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEdit = (idx: number) => {
    const owner = owners[idx];
    setFirstName(owner.firstName);
    setMiddleName(owner.middleName || "");
    setLastName(owner.lastName);
    setEmail(owner.email);
    const [year, month, day] = owner.dateOfBirth.split("-");
    setDate({ year, month, day });
    setStreetAddress(owner.streetAddress);
    setCity(owner.city);
    setSelectedState(owner.state);
    setPostalCode(owner.postalCode);
    setSelectedCountry(owner.country);
    setPercentageOfOwnership(owner.percentageOfOwnership);
    setPlaceOfBirth(owner.placeOfBirth || "");
    setNationality(owner.nationality || "");
    setCountryOfResidence(owner.countryOfResidence || "");
    if (owner.identityDocumentUrl) {
      setUploadedDocument([{ filename: 'Uploaded Document', url: owner.identityDocumentUrl }]);
    } else {
      setUploadedDocument([]);
    }
    setPendingFiles([]);
    setEditingIndex(idx);
    setShowAddForm(true);
  };

  const handleRemove = async (idx: number) => {
    if (window.confirm("Are you sure you want to remove this owner?")) {
      const updatedOwners = owners.filter((_, i) => i !== idx);
      setOwners(updatedOwners);
      if (editingIndex === idx) {
        setEditingIndex(null);
        resetForm();
        setShowAddForm(false);
      }
      await updateEntityKycDraftStep(ssoUserID, "beneficialOwners", updatedOwners);
    }
  };

  const handleAddOwner = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      let updatedDocuments = [...uploadedDocument];
      if (pendingFiles.length > 0) {
        const uploadedNewFiles = await uploadEntityKycDraftFiles(ssoUserID, pendingFiles);
        if (uploadedNewFiles) {
          updatedDocuments = [...updatedDocuments, ...uploadedNewFiles];
        }
      }

      const newOwner: Owner = {
        firstName,
        middleName,
        lastName,
        email,
        dateOfBirth: date.year + "-" + date.month + "-" + date.day,
        streetAddress,
        city,
        state: selectedState,
        postalCode,
        country: selectedCountry,
        percentageOfOwnership,
      };

      const p = parseFloat(percentageOfOwnership);
      if (!isNaN(p) && p > 20) {
        newOwner.placeOfBirth = placeOfBirth;
        newOwner.nationality = nationality;
        newOwner.countryOfResidence = countryOfResidence;
        newOwner.identityDocumentUrl = updatedDocuments[0]?.url || "";
      }

      if (editingIndex !== null) {
        const updatedOwners = [...owners];
        updatedOwners[editingIndex] = newOwner;
        setOwners(updatedOwners);
        setEditingIndex(null);
        await updateEntityKycDraftStep(ssoUserID, "beneficialOwners", updatedOwners);
      } else {
        const updatedOwners = [...owners, newOwner];
        setOwners(updatedOwners);
        await updateEntityKycDraftStep(ssoUserID, "beneficialOwners", updatedOwners);
      }

      resetForm();
      setShowAddForm(false);
    } catch (error) {
      console.error(error);
      notifyError("Failed to add beneficial owner");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextStep = async () => {
    if (owners.length === 0 && !showAddForm) {
      notifyError("Please add at least one Beneficial Owner");
      return;
    }
    if (checkPage === 'SummaryPage') {
      dispatch(setCheckPage(''));
      dispatch(setwizardPageName("summary"));
      dispatch(setwizardPageNumber(7));
    } else {
      dispatch(setwizardPageName("directors"));
      dispatch(setwizardPageNumber(6));
    }
  };

  const showMoreFields = parseFloat(percentageOfOwnership) > 20;

  return (
    <div className="pt-0 pb-3 mb-5 col-md-11 mx-auto px-2">
      <div className="row justify-content-center">
        <Col md={11}>
          <p className="fw-bold d-inline">{t('kyc.entity_account_app')}</p>
          <p className="text-warning d-inline ms-3 small">{t('common.in_progress')}</p>
          <h4 className="mt-3">Beneficial Owners</h4>
          <p className="text-secondary small">
            Please provide details for all beneficial owners.
          </p>
        </Col>
      </div>

      <div className="row justify-content-center mt-3">
        <Col md={11}>
          {owners.map((owner, idx) => (
            <Card key={idx} className="mb-3 rounded-4 shadow-sm border-0">
              <Card.Body className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">{owner.firstName} {owner.lastName} ({owner.percentageOfOwnership}%)</h5>
                  <p className="text-muted small mb-0">{owner.email}</p>
                </div>
                <div>
                  <Button variant="outline-primary" size="sm" className="me-2 rounded-5 px-3" onClick={() => handleEdit(idx)}>Edit</Button>
                  <Button variant="outline-danger" size="sm" className="rounded-5 px-3" onClick={() => handleRemove(idx)}>Remove</Button>
                </div>
              </Card.Body>
            </Card>
          ))}
          
          {!showAddForm && (
            <div className="d-flex justify-content-between align-items-center mt-2 mb-4">
              <Button variant="outline-primary" className="rounded-5 px-4" onClick={() => setShowAddForm(true)}>
                + Add Another Owner
              </Button>
              {owners.length > 0 && (
                <Button className="btn btn-success rounded-5" onClick={handleNextStep} style={{ minWidth: '200px' }}>
                  Continue
                </Button>
              )}
            </div>
          )}

          {showAddForm && (
            <div className="bg-light p-4 rounded-4 mb-4">
              <h5 className="mb-4">{editingIndex !== null ? "Edit Beneficial Owner" : "Add Beneficial Owner"}</h5>
              <div className="row mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold">First Name *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} isInvalid={!!errors.firstName} />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Middle Name</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="text" value={middleName} onChange={e => setMiddleName(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Last Name *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="text" value={lastName} onChange={e => setLastName(e.target.value)} isInvalid={!!errors.lastName} />
                  </Form.Group>
                </Col>
              </div>

              <div className="row mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Email Address *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="email" value={email} onChange={e => setEmail(e.target.value)} isInvalid={!!errors.email} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Percentage of Ownership (%) *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="number" value={percentageOfOwnership} onChange={e => setPercentageOfOwnership(e.target.value)} isInvalid={!!errors.percentageOfOwnership} />
                  </Form.Group>
                </Col>
              </div>

              <div className="row mb-3 mt-4">
                <Col md={12}>
                    <label className="form-label fw-bold">Date of Birth *</label>
                    <div className="d-flex align-items-center">
                        <div className="me-3">
                            <MonthPicker defaultValue={'Month'} numeric short caps endYearGiven year={date.year as any} value={date.month as any} onChange={(m: any) => setDate({ ...date, month: m })} classes={'form-select rounded-5 py-3'} />
                        </div>
                        <div className="me-3">
                            <DayPicker defaultValue={'Date'} year={date.year as any} month={date.month as any} endYearGiven value={date.day as any} onChange={(d: any) => setDate({ ...date, day: d })} classes={'form-select rounded-5 py-3'} />
                        </div>
                        <div>
                            <YearPicker defaultValue={'Year'} start={1900} end={new Date().getFullYear()} reverse value={date.year as any} onChange={(y: any) => setDate({ ...date, year: y })} classes={'form-select rounded-5 py-3'} />
                        </div>
                    </div>
                    {errors.dob && <small className="text-danger">{errors.dob}</small>}
                </Col>
              </div>

              <h6 className="mt-4">Address Details</h6>
              <div className="row mb-3 mt-3">
                <Col md={6}>
                  <Form.Group>
                    <CountryDropdown selectedCountry={selectedCountry} handleCountryClick={setSelectedCountry} countryValidation={!!errors.country} countriesData={countriesData} title="Country *" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mt-4">
                    <Form.Label className="fw-bold">State / Province *</Form.Label>
                    <div className="dropdown">
                      <button className="btn w-100 bg-white border text-start d-flex justify-content-between align-items-center py-2 rounded-5" type="button" data-bs-toggle="dropdown">
                        {selectedState || "Select State"} <i className="fas fa-chevron-down text-muted"></i>
                      </button>
                      <ul className="dropdown-menu w-100 shadow" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {countriesStateData.map((c: any) => 
                          c.name === selectedCountry && c.states?.map((s: any) => (
                            <li key={s.id} onClick={() => setSelectedState(s.name)}>
                              <span className="dropdown-item" style={{ cursor: 'pointer' }}>{s.name}</span>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                    {errors.state && <small className="text-danger">{errors.state}</small>}
                  </Form.Group>
                </Col>
              </div>
              <div className="row mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">City *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="text" value={city} onChange={e => setCity(e.target.value)} isInvalid={!!errors.city} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Postal Code *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="text" value={postalCode} onChange={e => setPostalCode(e.target.value)} isInvalid={!!errors.postalCode} />
                  </Form.Group>
                </Col>
              </div>
              <div className="row mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Street Address *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="text" value={streetAddress} onChange={e => setStreetAddress(e.target.value)} isInvalid={!!errors.streetAddress} />
                  </Form.Group>
                </Col>
              </div>

              {showMoreFields && (
                <>
                  <hr className="my-4" />
                  <h6 className="mt-2 text-primary">Additional Information (Ownership &gt; 20%)</h6>
                  <div className="row mb-3 mt-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Place of Birth *</Form.Label>
                        <Form.Control className="rounded-5 py-2" type="text" value={placeOfBirth} onChange={e => setPlaceOfBirth(e.target.value)} isInvalid={!!errors.placeOfBirth} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Nationality *</Form.Label>
                        <Form.Control className="rounded-5 py-2" type="text" value={nationality} onChange={e => setNationality(e.target.value)} isInvalid={!!errors.nationality} />
                      </Form.Group>
                    </Col>
                  </div>
                  <div className="row mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <CountryDropdown selectedCountry={countryOfResidence} handleCountryClick={setCountryOfResidence} countryValidation={!!errors.countryOfResidence} countriesData={countriesData} title="Country of Residence *" />
                      </Form.Group>
                    </Col>
                  </div>
                  <div className="row mb-4 mt-3">
                    <Col md={12}>
                      <Form.Label className="fw-bold">Identity Document *</Form.Label>
                      <MultiFileSelector
                        maxFiles={1}
                        maxFileSizeMB={20}
                        existedFiles={uploadedDocument}
                        onFilesChange={(f: any) => setUploadedDocument(f)}
                        onPendingFilesChange={(f: any) => setPendingFiles(f)}
                      />
                      {errors.document && <small className="text-danger">{errors.document}</small>}
                    </Col>
                  </div>
                </>
              )}

              <div className="d-flex justify-content-end gap-3 mt-4">
                {owners.length > 0 && (
                  <Button variant="outline-secondary" className="rounded-5 px-4" onClick={() => { setShowAddForm(false); setEditingIndex(null); resetForm(); }}>
                    Cancel
                  </Button>
                )}
                <Button className="btn btn-primary rounded-5 px-4" onClick={handleAddOwner} disabled={isLoading}>
                  {isLoading ? "Saving..." : "Save Owner"}
                </Button>
              </div>
            </div>
          )}



        </Col>
      </div>
    </div>
  );
};

export default BeneficialOwnersForm;
